import React from 'react';
import { Box, TextField, useTheme } from '@mui/material';

interface NotebookEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  paperColor?: 'white' | 'yellow';
}

const NotebookEditor: React.FC<NotebookEditorProps> = ({
  content,
  onChange,
  placeholder = 'Start writing...',
  readOnly = false,
  paperColor = 'yellow',
}) => {
  const theme = useTheme();

  const bgColor = paperColor === 'yellow' ? '#FFFEF0' : '#FFFFFF';
  const lineColor = paperColor === 'yellow' ? '#E8D5B7' : '#E0E0E0';
  const marginColor = paperColor === 'yellow' ? '#F5C6C6' : '#FFCCCC';

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: bgColor,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        minHeight: 500,
      }}
    >
      {/* Red margin line */}
      <Box
        sx={{
          position: 'absolute',
          left: 60,
          top: 0,
          bottom: 0,
          width: 2,
          backgroundColor: marginColor,
          zIndex: 1,
        }}
      />

      {/* Spiral binding holes */}
      <Box
        sx={{
          position: 'absolute',
          left: 20,
          top: 0,
          bottom: 0,
          width: 20,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          pt: 2,
          gap: 3,
        }}
      >
        {Array.from({ length: 20 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: '#333',
              opacity: 0.2,
            }}
          />
        ))}
      </Box>

      {/* Lined paper background */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          backgroundImage: `repeating-linear-gradient(
            transparent,
            transparent 31px,
            ${lineColor} 31px,
            ${lineColor} 32px
          )`,
          backgroundPosition: '0 8px',
          pointerEvents: 'none',
        }}
      />

      {/* Text area */}
      <TextField
        fullWidth
        multiline
        variant="standard"
        placeholder={placeholder}
        value={content}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        InputProps={{
          disableUnderline: true,
          sx: {
            fontFamily: '"Caveat", "Patrick Hand", "Indie Flower", cursive',
            fontSize: '1.3rem',
            lineHeight: '32px',
            color: '#333',
            padding: '16px 24px 16px 80px',
            minHeight: 500,
            alignItems: 'flex-start',
            '& textarea': {
              lineHeight: '32px !important',
              '&::placeholder': {
                fontStyle: 'italic',
                opacity: 0.5,
              },
            },
          },
        }}
        sx={{
          position: 'relative',
          zIndex: 2,
          '& .MuiInputBase-root': {
            backgroundColor: 'transparent',
          },
        }}
      />
    </Box>
  );
};

export default NotebookEditor;

